import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import PaymentServiceTerminal, {
  TerminalEventMonitoring,
  EVENT_TYPES,
} from 'app/src/payments/terminals/PaymentServiceTerminal';
import StripeTerminalService, {
  UnhandledStripeServiceError,
  StripeTerminalPaymentStatus,
} from 'app/src/payments/terminals/StripeTerminalService';
import Localize from 'app/src/localize/Localize';
import Payment from 'app/src/payments/Payment';
import PaymentModel from 'app/src/data/models/Payment';
import Format from 'app/src/data/Format';
import $ from 'jquery';
import get from 'lodash/get';

const _MINIMUM_POLLING_INTERVAL = 1000; // 1 second
const _DESCRIPTION = 'Lightspeed Payments Terminal Powered by Stripe';

export default class StripeTerminal extends PaymentServiceTerminal {
  constructor(terminalModel) {
    super(terminalModel, _MINIMUM_POLLING_INTERVAL);

    this.terminalModel = terminalModel;
    this.terminalResolvedPayment = false;
    this.terminalEventMonitoring = null;
    this.stripeTerminalServiceInstance = null;
  }

  /**
   * Returns a string describing the terminal.
   *
   * @returns {string}    A description of the terminal
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   *  - Initialize the Stripe Terminal
   *  - Check the configuration
   *
   * @param {JQuery.promise} deferred
   */
  init() {
    let deferred = $.Deferred();

    if (!this.stripeTerminalServiceInstance) {
      StripeTerminalService.getInstance()
        .done(StripeTerminalServiceInstance => {
          this.stripeTerminalServiceInstance = StripeTerminalServiceInstance;
          deferred.resolve();
        })
        .fail(instanceError => {
          deferred.reject(instanceError);
        });
    } else {
      deferred.resolve();
    }

    return deferred;
  }

  /**
   * Re-initialize some of the class attributes
   * and disconnected the terminal
   */
  reset() {
    super.reset();
    this.terminalResolvedPayment = false;

    // We disconnect terminal if the terminal is connected.
    if (
      this.stripeTerminalServiceInstance &&
      this.stripeTerminalServiceInstance.getConnectionStatus() !== 'not_connected'
    ) {
      this._disconnectReader();
    }

    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  Optional - when true the terminal will be prompt for a manual
   *                                 entry (when available)
   * @param {Boolean} paymentOptions.isUnreferencedRefund Optional - when true the unreferenced refund flow will be
   *                                                      triggered
   */
  processPayment(paymentOptions = {}) {
    let deferred = $.Deferred();

    const { amount, refundedPaymentID, isUnreferencedRefund } = paymentOptions;
    const gatewayParameters = get(paymentOptions, 'gatewayConfig.attributes.parameters');

    // Initiate the monitoring here instead of inside the constructor
    // to avoid catching too many events from instantiation
    this.terminalEventMonitoring = new TerminalEventMonitoring(
      get(this.terminalModel, 'attributes'),
      'StripeTerminal'
    );
    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STARTED);

    // We reset the resolve Payment Flag every time we process or retry a payment
    this.terminalResolvedPayment = false;

    let unreferencedDeferred = $.Deferred();
    if (isUnreferencedRefund) {
      // For unreferenced refunds, we need to obtain a Payment Method ID which we send to
      // the API when staging the payment
      this.init()
        .done(() => {
          this._processUnreferencedRefund(gatewayParameters)
            .done(response => {
              unreferencedDeferred.resolve(get(response, 'payment_method.id'));
            })
            .fail(processUnreferencedRefundError => {
              if (this._isUnhandledStripeServiceError(processUnreferencedRefundError)) {
                deferred.reject(processUnreferencedRefundError);
              }
              unreferencedDeferred.reject();
            });
        })
        .fail(instanceUnhandledError => {
          this._handleServiceError(instanceUnhandledError, deferred);
        });
    } else {
      // nothing to do; move along
      unreferencedDeferred.resolve();
    }

    const pollForResponse = ({ payment, terminalPayment }) => {
      this.pollApiForPaymentStatus(payment)
        .done(() => {
          const paymentStatus = payment.get('status');
          const paymentTransaction = payment.paymentTransaction;

          this.terminalEventMonitoring.paymentStatus = paymentStatus;
          this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.CAPTURED);

          // When done we need to disconnect the reader
          this._disconnectReader();

          // Save the model for later
          terminalPayment.serviceModel = payment;
          terminalPayment.model.set(
            'amount',
            Format.parseDecimal(payment.get('totalAmount'), terminalPayment.amount)
          );

          switch (paymentStatus) {
            case PaymentModel.Status.APPROVED: // When the payment is approved
              deferred.resolve(
                new TerminalTransactionResponse(
                  Terminal.Response.APPROVED,
                  paymentStatus,
                  terminalPayment
                )
              );
              break;

            default:
              // When the payment status is unsupported we throw a terminal error
              const paymentResponseMessage = paymentTransaction
                ? paymentTransaction.get('message') || ''
                : '';

              deferred.reject(
                new TerminalTransactionResponse(
                  Terminal.Response.TERMINAL_ERROR,
                  paymentStatus,
                  terminalPayment,
                  paymentResponseMessage
                )
              );
              break;
          }
        })
        .fail(unhandledError => {
          // When done we need to disconnect the reader
          this._disconnectReader();
          terminalPayment.serviceModel = payment;

          if (unhandledError) {
            this._handleServiceError(unhandledError, deferred, terminalPayment);
          } else {
            const paymentStatus = payment.get('status');
            const paymentTransaction = payment.paymentTransaction;

            const paymentResponseMessage = paymentTransaction
              ? paymentTransaction.get('message') || ''
              : '';

            // Deal with all the handledError status coming back from the pay-service
            switch (paymentStatus) {
              case PaymentModel.Status.DECLINED: // When a payment is declined
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.DECLINED,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case PaymentModel.Status.ERROR: // When a payment is in error
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.ERROR,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case PaymentModel.Status.CANCELLED: // When a payment transaction is cancelled
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.CANCELLED_FROM_POS,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case PaymentModel.Status.UNKNOWN: // When a payment is having an UNKNOWN error
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.UNKNOWN,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
              default:
                // When the payment status is unsupported we throw a service exception
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.SERVICE_EXCEPTION,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
            }
          }
        })
        .always(() => {
          this.terminalEventMonitoring.paymentStatus = payment.get('status');
          this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.COMPLETED);
        });
    };

    $.when(unreferencedDeferred).always(cardToken => {
      this.init()
        .done(() => {
          // We try to discover the reader first before staging a payment
          // to avoid creating a payment for nothing.
          this._discoverReaders(gatewayParameters)
            .done(selectedReader => {
              this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STAGING);
              this.stagePayment({
                ...paymentOptions,
                workflow:
                  refundedPaymentID && amount.isNegative()
                    ? PaymentModel.Workflow.TERMINAL_REFUNDS
                    : PaymentModel.Workflow.TERMINAL_PAYMENTS,
                cardToken,
              })
                .done(({ payment, terminalPayment }) => {
                  this.terminalEventMonitoring.paymentId = payment.get('paymentID');
                  this.terminalEventMonitoring.paymentStatus = payment.get('status');
                  this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STAGED);

                  if (isUnreferencedRefund) {
                    pollForResponse({ payment, terminalPayment });
                  } else {
                    this._connectReader(selectedReader)
                      .done(() => {
                        pollForResponse({ payment, terminalPayment });
                      })
                      .fail(connectReaderUnhandledError => {
                        // We want to cancel any staged payment in case of
                        // terminal connection failure to remove the lock.
                        this.cancelPayment(payment)
                          .then(() => {
                            this._handleServiceError(
                              connectReaderUnhandledError,
                              deferred,
                              terminalPayment
                            );
                          })
                          .fail(cancelError => {
                            // Send this uncaught cancel exception to new relic
                            this.terminalEventMonitoring.submitError(
                              cancelError,
                              'cancel_exception'
                            );

                            deferred.reject(
                              new TerminalTransactionResponse(
                                Terminal.Response.ERROR,
                                Terminal.Status.ERROR,
                                terminalPayment,
                                cancelError
                              )
                            );
                          });
                      });
                  }
                })
                .fail(transactionResponse => {
                  deferred.reject(transactionResponse);
                });
            })
            .fail(discoverReadersUnhandledError => {
              this._handleServiceError(discoverReadersUnhandledError, deferred);
            });
        })
        .fail(instanceUnhandledError => {
          this._handleServiceError(instanceUnhandledError, deferred);
        });
    });

    return deferred.promise();
  }

  /**
   * Cancels payment at the Pay-Service level or Stripe Terminal level
   * Rules for Cancelling at Pay-service level:
   * we will try to cancel the payment at pay service level.
   * After we run keepPolling we will receive canceled action from payments, which will clear the state from stripe
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  cancelPayment(payment) {
    let deferred = $.Deferred();
    super
      .cancelPayment(payment)
      .done(() => {
        this._keepPolling(payment, deferred);
        deferred.resolve();
      })
      .fail(err => {
        deferred.reject(err);
      });
    return deferred.promise();
  }

  /**
   * Polls the status of the specified payment at a regular interval.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment The payment on which the status is currently unknown
   * @returns {*}
   */
  pollApiForPaymentStatus(payment) {
    let deferred = $.Deferred();

    this._elapsedTime = 0;
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._getPaymentStatus(payment, deferred);

    return deferred.promise();
  }

  /**
   * This method deals with errors that are not handled by the partner service
   *
   * @param {object} unhandledError that contains unhandledErrorCode and unhandledErrorMessage
   * @param {JQuery.deferred} deferred
   * @param {object} terminalPayment optional
   */
  _handleServiceError(error, deferred, terminalPayment = null) {
    const { code: unhandledErrorCode, message: unhandledErrorMessage } = error;

    this.terminalEventMonitoring.submitError(error);
    if (unhandledErrorMessage) {
      switch (unhandledErrorCode) {
        case UnhandledStripeServiceError.EXPIRED_CARD:
        case UnhandledStripeServiceError.INVALID_EXPIRY_MONTH:
        case UnhandledStripeServiceError.INVALID_EXPIRY_YEAR:
        case UnhandledStripeServiceError.READER_ERROR:
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.TERMINAL_ERROR,
              Terminal.Status.ERROR,
              terminalPayment,
              unhandledErrorMessage
            )
          );
          break;
        case UnhandledStripeServiceError.PAYMENT_INTENT_UNEXPECTED_STATE:
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.SERVICE_EXCEPTION,
              Terminal.Status.ERROR,
              terminalPayment,
              Localize.translate(
                `There was a problem capturing the previous payment. You cannot attempt the same payment a second time.\n If you cannot finalize the sale please contact support.`
              )
            )
          );
          break;
        case UnhandledStripeServiceError.CANCELED:
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.CANCELLED_FROM_POS,
              Terminal.Status.DISCONNECTED,
              terminalPayment,
              unhandledErrorMessage
            )
          );
          break;
        case UnhandledStripeServiceError.SERVICE_EXCEPTION:
        default:
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.SERVICE_EXCEPTION,
              Terminal.Status.ERROR,
              terminalPayment,
              unhandledErrorMessage
            )
          );
          break;
      }
    } else {
      deferred.reject(
        new TerminalTransactionResponse(
          Terminal.Response.ERROR,
          Terminal.Status.ERROR,
          terminalPayment,
          unhandledErrorMessage ||
            Localize.translate(
              `Unable to process the payment. An unknown error occurred with the terminal, you can re-attempt the payment. If the problem persists please contact support.`
            )
        )
      );
    }
  }

  /**
   * Initiate the setTimeout for the polling that will calls
   * the update of the payment status at every nextPollingInterval
   *
   * @param {Payment} payment
   * @param {JQueryDeferred} deferred promise
   */
  _keepPolling(payment, deferred) {
    // We opt to use setTimeout rather than setInterval since we
    // want to avoid the potential case where the fetch from the API takes longer
    // than the polling interval.
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
    }

    this._pollingTimer = setTimeout(() => {
      this._elapsedTime += this.nextPollingInterval(this._elapsedTime);
      this._getPaymentStatus(payment, deferred);
    }, this.nextPollingInterval(this._elapsedTime));
  }

  /**
   * Kills the pollingTimer if available
   */
  _stopPolling() {
    if (this._pollingTimer) {
      delete this._pollingTimer;
    }
  }

  /**
   * Do the logic to discover to Stripe Reader
   *
   * @returns {JQueryDeferred} deferred.promise();
   */
  _discoverReaders(gatewayParameters) {
    let deferred = $.Deferred();

    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.DISCOVERING);

    this.stripeTerminalServiceInstance
      .discoverReaders(gatewayParameters)
      .done(({ discoveredReaders }) => {
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.DISCOVERED);
        const [readerFound] = discoveredReaders.filter(
          reader =>
            reader.serial_number ===
            get(this.terminalModel, 'attributes.configuration.serialNumber')
        );

        if (readerFound) {
          // Set terminal information when available
          this.terminalEventMonitoring.terminal = readerFound;

          deferred.resolve(readerFound);
        } else {
          deferred.reject({
            code: UnhandledStripeServiceError.READER_REGISTER_ERROR,
            message: `The payment terminal is not fully registered to this shop.\n Please, retry the pairing process under:\n Settings > Payment Processing > Configure.\n If you still experience the issue after the re-pairing process,\n please contact the support team.`,
          });
        }
      })
      .fail(discoverReadersError => {
        // Return the errorMessage when the discoverReaders fails
        deferred.reject(discoverReadersError);
      });

    return deferred.promise();
  }

  /**
   * Do the logic to connect to Stripe Reader
   *
   * @returns {JQueryDeferred} deferred.promise();
   */
  _connectReader(selectedReader) {
    let deferred = $.Deferred();

    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.CONNECTING);

    // We disconnect terminal if the terminal is connected.
    if (this.stripeTerminalServiceInstance.getConnectionStatus() !== 'not_connected') {
      this._disconnectReader();
    }

    this.stripeTerminalServiceInstance
      .connectReader(selectedReader)
      .done(() => {
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.CONNECTED);
        deferred.resolve();
      })
      .fail(connectReadError => {
        // Return the errorMessage when the connectReader fails
        deferred.reject(connectReadError);
      });

    return deferred.promise();
  }

  /**
   * Disconnect any Stripe Reader currently connected to
   * the current Instance of the Stripe Terminal Service.
   */
  _disconnectReader() {
    this.stripeTerminalServiceInstance.disconnectReader();
  }

  /**
   * Process a payment on the Stripe Terminal Service by doing the following:
   * 1. Collect the payment method for the specified paymentToken
   * 2. Process the payment intent returned by the collect payment method
   *
   * @param {String} paymentToken
   */
  _processPayment(paymentToken) {
    let deferred = $.Deferred();

    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTING);
    this.stripeTerminalServiceInstance
      .collectPaymentMethod(paymentToken)
      .done(({ paymentIntent }) => {
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTED);
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.PROCESSING);
        this.stripeTerminalServiceInstance
          .processPaymentIntent(paymentIntent)
          .done(() => {
            this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.PROCESSED);
            deferred.resolve();
          })
          .fail(processPaymentIntentError => {
            deferred.reject(processPaymentIntentError);
          });
      })
      .fail(collectPaymentMethodError => {
        deferred.reject(collectPaymentMethodError);
      });

    // We set the resolve Payment Flag to true in order
    // to not restart indefinitely the _processPayment.
    this.terminalResolvedPayment = true;

    return deferred.promise();
  }

  /**
   * Process a refund on the Stripe Terminal Service by doing the following:
   * 1. Collect the refund payment method for the specified refundChargeId and amount
   * 2. Process the refund
   *
   * @param {String} refundChargeId
   * @param {Decimal} refundAmount
   */
  _processRefund(refundChargeId, refundAmount) {
    let deferred = $.Deferred();

    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTING);
    this.stripeTerminalServiceInstance
      .collectRefundPaymentMethod(
        refundChargeId,
        refundAmount
          .abs()
          .multipliedBy(100)
          .toNumber()
      )
      .done(() => {
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTED);
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.PROCESSING);
        this.stripeTerminalServiceInstance
          .processRefund()
          .done(() => {
            this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.PROCESSED);
            deferred.resolve();
          })
          .fail(processRefundError => {
            // Return the errorMessage when the processRefund fails
            deferred.reject(processRefundError);
          });
      })
      .fail(collectRefundPaymentMethodError => {
        // Return the errorMessage when the collectRefundPaymentMethod fails
        deferred.reject(collectRefundPaymentMethodError);
      });

    // We set the resolve Payment Flag to true in order
    // to not restart indefinitely the _processRefund.
    this.terminalResolvedPayment = true;

    return deferred.promise();
  }

  /**
   * Process an unreferenced refund on the Stripe Terminal Service by doing the following:
   * 1. Connect to the reader
   * 2. Collect the refund payment method
   *
   */
  _processUnreferencedRefund(gatewayParameters) {
    let deferred = $.Deferred();

    this._discoverReaders(gatewayParameters)
      .done(selectedReader => {
        this._connectReader(selectedReader)
          .done(() => {
            this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTING);
            this.stripeTerminalServiceInstance
              .collectUnreferencedRefundPaymentMethod()
              .done(response => {
                this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.TERMINAL.COLLECTED);
                deferred.resolve(response);
              })
              .fail(collectRefundPaymentMethodError => {
                // Return the errorMessage when the collectRefundPaymentMethod fails
                deferred.reject(collectRefundPaymentMethodError);
              })
              .always(() => {
                this._disconnectReader();
              });

            // We set the resolve Payment Flag to true in order
            // to not restart indefinitely the _processRefund.
            this.terminalResolvedPayment = true;
          })
          .fail(connectReaderUnhandledError => {
            this._handleServiceError(connectReaderUnhandledError, deferred);
          });
      })
      .fail(discoverReadersUnhandledError => {
        this._handleServiceError(discoverReadersUnhandledError, deferred);
      });

    return deferred.promise();
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment who's status is currently unknown
   * @param {Deferred} deferred   The deferred object that _pollApiForPaymentStatus uses to determine
   *                              when the status of the payment has been resolved.
   * @private
   */
  _getPaymentStatus(payment, deferred) {
    payment
      .fetch()
      .done(() => {
        this.goodPoll();

        if (payment instanceof Payment.constructor) {
          deferred.reject(
            new Error(
              `Unable to process the current payment. The payment you are trying to process is invalid. You might want to start a new sale or contact support.`
            )
          );
          this._stopPolling();
        }

        switch (payment.get('status')) {
          case PaymentModel.Status.APPROVED:
            this._stopPolling();
            deferred.resolve();
            break;

          case PaymentModel.Status.PENDING:
            const paymentToken = get(
              payment.get('cardPaymentRequest'),
              'parameters.paymentToken',
              null
            );
            const amount = Format.parseMoney(get(payment.get('SalePayment'), 'amount'));

            if (!amount) {
              deferred.reject(
                new Error(
                  `Unable to process the current payment. The amount of the payment you are trying to process is invalid. You might want to start a new sale or contact support.`
                )
              );
              this._stopPolling();
            }

            if (paymentToken && !this.terminalResolvedPayment) {
              const event = new CustomEvent(Terminal.TerminalEvents.PAYMENT_STARTED, {
                detail: { payment },
              });
              window.dispatchEvent(event);
              if (this._isCardPaymentRequestRefund(paymentToken, amount)) {
                //
                // referenced refund
                //
                this._processRefund(paymentToken, amount)
                  .done(() => {
                    this._keepPolling(payment, deferred);
                  })
                  .fail(processRefundError => {
                    if (this._isUnhandledStripeServiceError(processRefundError)) {
                      // If the error is unhandled by a webhook,
                      // we cancel payment and throw the error to the UI
                      if (this._isCancelableError(processRefundError)) {
                        this.cancelPayment(payment)
                          .then(() => {
                            deferred.reject(processRefundError);
                          })
                          .fail(cancelError => {
                            deferred.reject(cancelError);
                          });
                      } else {
                        deferred.reject(processRefundError);
                      }

                      this._stopPolling();
                    } else {
                      // If the error is handled by a webhook we continue the polling
                      // and leave the resolution to our services.
                      this._keepPolling(payment, deferred);
                    }
                  });
              } else {
                //
                // typical payment
                //
                this._processPayment(paymentToken)
                  .done(() => {
                    this._keepPolling(payment, deferred);
                  })
                  .fail(processPaymentError => {
                    if (this._isUnhandledStripeServiceError(processPaymentError)) {
                      // If the error is unhandled by a webhook,
                      // we cancel payment and throw the error to the UI
                      if (this._isCancelableError(processPaymentError)) {
                        this.cancelPayment(payment)
                          .then(() => {
                            deferred.reject(processPaymentError);
                          })
                          .fail(cancelError => {
                            deferred.reject(cancelError);
                          });
                      } else {
                        deferred.reject(processPaymentError);
                      }
                      this._stopPolling();
                    } else {
                      // If the error is handled by a webhook we continue the polling
                      //  and leave the resolution to our services.
                      this._keepPolling(payment, deferred);
                    }
                  });
              }
            } else {
              this._keepPolling(payment, deferred);
            }
            break;
          case PaymentModel.Status.CANCELLED:
            /* When we cancel a transaction from the POS,
             we do so by cancelling the payment on payment side (this.cancelPayment)
             Next step we clear the payment on stripe terminal by calling this.keepPolling which after acquiring payment status
             ( confirming its actually canceled) invokes the cancelCollectPaymentMethod.
             Note: Until we can refactor, cancelling on the terminal( by customer), calls this.Cancel as well and that calls, the method below,
                  we check if the terminal is actually in a state to accept clear
            */
            this._stopPolling();
            if (
              this.stripeTerminalServiceInstance.getPaymentStatus() ===
              StripeTerminalPaymentStatus.WAITING_FOR_INPUT
            ) {
              this.stripeTerminalServiceInstance
                .cancelCollectPaymentMethod()
                .then(() => {
                  deferred.resolve();
                })
                .catch(err => {
                  deferred.reject(
                    new Error(
                      `Something went wrong while trying to abort the payment. If the problem persists please contact support.`
                    )
                  );
                });
            } else {
              deferred.reject();
            }
            break;
          default:
            deferred.reject();
            this._stopPolling();
            break;
        }
      })
      .fail(() => {
        this.badPoll();
        this._keepPolling(payment, deferred);
      });
  }

  /**
   * Check if the current payment request is a refund
   * 1. If payment_token starts with 'ch_'
   * 2. Check if the amount is negative
   *
   * @param {String} paymentToken extracted from the Payment
   * @param {Decimal} amount extracted from the Payment
   */
  _isCardPaymentRequestRefund(paymentToken = '', amount) {
    return !!(paymentToken && paymentToken.match(/^ch_/) && amount && amount.isNegative());
  }

  /**
   * Check if the error returned is handled or not
   * by a Stripe webhook
   *
   * @param {object} unhandledError that contains a code and message associated to the error
   *
   * @returns {boolean} Return true if the error code is found in the list of UnhandledStripeServiceError
   */
  _isUnhandledStripeServiceError({ code: unhandledErrorCode }) {
    // We check:
    // 1. The unhandledErrorCode is falsy
    // 2. Or the unhandledErrorCode is in the list of UnhandledStripeServiceError

    return (
      !unhandledErrorCode || Object.values(UnhandledStripeServiceError).includes(unhandledErrorCode)
    );
  }

  /**
   * Check if the Stripe's payment is in a cancelable error state
   *
   * PAYMENT_INTENT_UNEXPECTED_STATE and CANCELED are the two states that have currently been identified
   * as a problematic state that would prevent a cancellation of the payment.
   *
   * CANCELED is reached when POS cancels the payment on terminal.
   * Maybe more error states could be added here in the future.
   *
   * @param {object} unhandledError that contains a code and message associated to the error
   *
   * @returns {boolean} Return true if the error code is found in the list of UnhandledStripeServiceError
   */
  _isCancelableError({ code: unhandledErrorCode }) {
    return ![
      UnhandledStripeServiceError.PAYMENT_INTENT_UNEXPECTED_STATE,
      UnhandledStripeServiceError.CANCELED,
    ].includes(unhandledErrorCode);
  }
}
